import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PostEditor } from "../components/PostEditor";
import { LinkedInPreview } from "../components/LinkedInPreview";
import { ScheduleModal } from "../components/ScheduleModal";
import { CitationsList } from "../components/CitationsList";
import { ImageStudio } from "../components/ImageStudio";
import { AlternativesPanel } from "../components/AlternativesPanel";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Button } from "../components/ui/Button";
import { SkeletonList } from "../components/ui/Skeleton";
import { toast } from "../lib/toast";
import { useDraft } from "../hooks/useDraft";
import {
  useApproveMutation,
  useRejectMutation,
  useUpdateMutation,
} from "../hooks/useDraftMutations";

const DraftEditor = ({ draftId }: { draftId: string }) => {
  const { data: draft, isLoading, error } = useDraft(draftId);
  const navigate = useNavigate();
  const approveMut = useApproveMutation();
  const rejectMut = useRejectMutation();
  const updateMut = useUpdateMutation();

  const [linkedinText, setLinkedinText] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [savedAgo, setSavedAgo] = useState("");
  // Tracks whether the current linkedinText came from a real user edit (vs a
  // server re-sync). Autosave only fires when this is true, so a server-side
  // content change — e.g. promoting an alternative — is never PUT back.
  const dirtyRef = useRef(false);

  // Re-seed the editor text whenever the server content changes (draft id
  // change OR a refetch with new content, such as a promoted alternative).
  // Resetting dirty here prevents the debounced autosave from re-saving.
  useEffect(() => {
    if (!draft) return;
    setLinkedinText(draft.content);
    dirtyRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.content]);

  useEffect(() => {
    if (!draft || !dirtyRef.current || linkedinText === draft.content) return;
    const t = setTimeout(() => {
      dirtyRef.current = false;
      updateMut.mutate(
        { id: draftId, content: linkedinText },
        { onSuccess: () => setLastSavedAt(Date.now()) }
      );
    }, 1000);
    return () => clearTimeout(t);
  }, [linkedinText, draft?.content, draftId]);

  // Persist any pending user edit immediately (used before approve/navigate and
  // on unmount, so the last debounced edit is never dropped). Kept in a ref so
  // the unmount cleanup always sees the latest content.
  const flushRef = useRef<() => void>(() => {});
  flushRef.current = () => {
    if (draft && dirtyRef.current && linkedinText !== draft.content) {
      dirtyRef.current = false;
      updateMut.mutate(
        { id: draftId, content: linkedinText },
        { onSuccess: () => setLastSavedAt(Date.now()) }
      );
    }
  };

  useEffect(() => {
    return () => flushRef.current();
  }, []);

  // "salvo há Xs" refresher
  useEffect(() => {
    if (!lastSavedAt) return;
    const tick = () => {
      const sec = Math.floor((Date.now() - lastSavedAt) / 1000);
      if (sec < 5) setSavedAgo("agora");
      else if (sec < 60) setSavedAgo(`há ${sec}s`);
      else setSavedAgo(`há ${Math.floor(sec / 60)}min`);
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => clearInterval(iv);
  }, [lastSavedAt]);

  // Atalho ⌘S / Ctrl+S = save imediato (sem esperar debounce)
  useEffect(() => {
    if (!draft) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (linkedinText !== draft.content) {
          updateMut.mutate(
            { id: draftId, content: linkedinText },
            { onSuccess: () => setLastSavedAt(Date.now()) }
          );
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draft, linkedinText, draftId, updateMut]);

  const approveImmediate = () => {
    flushRef.current();
    approveMut.mutate(
      { id: draftId, scheduledAt: undefined },
      {
        onSuccess: () => {
          toast.success("Draft aprovado — publicando agora");
          navigate("/scheduled");
        },
        onError: (err: Error) => {
          toast.error("Falha ao aprovar", { description: err.message });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonList count={1} />
      </div>
    );
  }
  if (error) return <div className="text-sm text-red-600">Erro: {(error as Error).message}</div>;
  if (!draft) return null;

  const saveStatus: "idle" | "saving" | "saved" | "error" = updateMut.isPending
    ? "saving"
    : updateMut.isError
      ? "error"
      : lastSavedAt
        ? "saved"
        : "idle";

  const dotClass =
    saveStatus === "saving"
      ? "bg-amber-500 animate-pulse"
      : saveStatus === "saved"
        ? "bg-emerald-500"
        : saveStatus === "error"
          ? "bg-red-500"
          : "bg-zinc-300 dark:bg-zinc-600";

  const saveText =
    saveStatus === "saving"
      ? "Salvando…"
      : saveStatus === "saved"
        ? `Salvo ${savedAgo}`
        : saveStatus === "error"
          ? "Falha ao salvar"
          : "Não modificado";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Draft</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 flex items-center gap-2 flex-wrap">
            <span>
              {draft.format} · {draft.theme}
            </span>
            <span className="inline-flex items-center gap-1.5 ml-1">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`} aria-hidden="true" />
              <span className="text-xs">{saveText}</span>
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={() => setRejectOpen(true)}
            disabled={rejectMut.isPending}
          >
            Rejeitar
          </Button>
          <Button
            variant="secondary"
            onClick={approveImmediate}
            loading={approveMut.isPending}
          >
            Aprovar e publicar agora
          </Button>
          <Button variant="primary" onClick={() => setScheduleOpen(true)}>
            Aprovar e agendar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <PostEditor
            initialContent={draft.content}
            onChange={(text) => {
              dirtyRef.current = true;
              setLinkedinText(text);
            }}
          />
        </div>
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-slate-600">Preview LinkedIn</h2>
          <LinkedInPreview
            text={linkedinText}
            draftId={draftId}
            selectedImageId={draft.selectedImageId}
            imageCandidates={draft.imageCandidates}
          />
          <ImageStudio postId={draftId} />
          <AlternativesPanel draft={draft} />
          <CitationsList citations={draft.researchCitations} />
        </div>
      </div>

      <ScheduleModal
        isOpen={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onSubmit={(scheduledAt) => {
          flushRef.current();
          approveMut.mutate(
            { id: draftId, scheduledAt },
            {
              onSuccess: () => {
                setScheduleOpen(false);
                toast.success("Draft aprovado e agendado");
                navigate("/scheduled");
              },
              onError: (err: Error) => {
                toast.error("Falha ao aprovar", { description: err.message });
              },
            }
          );
        }}
      />

      <ConfirmDialog
        open={rejectOpen}
        title="Rejeitar este draft?"
        description="O draft será marcado como rejeitado e sairá da lista. Você pode regenerar depois."
        confirmLabel="Rejeitar"
        cancelLabel="Cancelar"
        destructive
        loading={rejectMut.isPending}
        onConfirm={() => {
          rejectMut.mutate(draftId, {
            onSuccess: () => {
              setRejectOpen(false);
              toast.success("Draft rejeitado");
              navigate("/drafts");
            },
            onError: (err: Error) => {
              toast.error("Falha ao rejeitar", { description: err.message });
            },
          });
        }}
        onCancel={() => setRejectOpen(false)}
      />
    </div>
  );
};

export default DraftEditor;
